/* THE WORLD HAS A SHAPE BEFORE IT HAS TILES.
 *
 * What this replaces: `generateScreen` thresholded two octaves of value noise
 * at frequencies 0.055 and 0.085 — wavelengths of about 18 and 12 tiles — into
 * water, wood and sand. That is a TEXTURE generator. It makes convincing
 * ground cover at one scale, everywhere, and structure at no scale, and the
 * measurements say so plainly: with the per-screen walls disabled a bare-handed
 * player reaches 97.9% of every walkable tile and 24 of 24 hollow screens. The
 * terrain partitioned nothing, which is why every barrier in the game had to
 * stand in a doorway — a doorway was the only narrow place in the world.
 *
 * So the order is the one a terrain generator normally uses, and every layer
 * is DERIVED rather than dropped in beside the others:
 *
 *   elevation   multi-octave, the low frequency dominant, so the map has two
 *               or three landforms rather than a hundred copses
 *   moisture    its own field, at its own scale
 *   sea         a level, not a threshold on a blob: water is where the land
 *               is below it, so lakes sit in basins
 *   rivers      traced DOWNHILL from high ground until they meet the sea or
 *               another river, which is what makes a river a line across a
 *               map instead of a stripe drawn on one
 *   cliffs      where the gradient is steep. This is the layer that produces
 *               chokepoints for free: a pass between two highlands is narrow
 *               because the highlands are wide
 *   biome       a function of (elevation, moisture), so a fen is low and wet
 *               and scree is high and dry, rather than a Voronoi cell that
 *               happens to be called one
 *
 * It is computed ONCE per world over the whole 128x96 tile map and handed to
 * `generateScreen` as a window, because a river that restarts at a screen
 * boundary is not a river. Pure, deterministic from the seed, no canvas — the
 * whole thing is checkable headless.
 */
import { mix, RNG } from './rng.ts';

/* THE LAST ARGUMENT NEEDS AVALANCHE, and `mix` does not give it any.
 *
 * `mix` is FNV-shaped: xor a term in, multiply, repeat. The final term is
 * xored and multiplied ONCE, so its effect never reaches the high bits — and
 * the high bits are exactly what `>>> 8` samples. Measured on the raw lattice
 * with the seed fixed: stepping x gives 0.3402, 0.4105, 0.5581, 0.4096, and
 * stepping y gives 0.3402, 0.3363, 0.3480, 0.3441. The y axis barely moves.
 *
 * The visible consequence is a world that varies across and not down — a
 * terrain map built on it comes out in vertical stripes, with sixteen tiles of
 * near-identical rows. NB this lattice is copied from `world.ts`, which has
 * the same fault and has had it all along.
 *
 * A finalizer fixes it: two xor-shift-multiply rounds, which is what every
 * integer hash ends with and what FNV is missing. */
function lattice(seed, ix, iy) {
  let h = mix(seed, ix + 1013, iy + 2027);
  h ^= h >>> 15; h = Math.imul(h, 2246822519) >>> 0;
  h ^= h >>> 13; h = Math.imul(h, 3266489917) >>> 0;
  h ^= h >>> 16;
  return (h >>> 8) / 16777216;
}
function smooth(t) { return t * t * (3 - 2 * t); }
function vnoise(seed, x, y) {
  const ix = Math.floor(x), iy = Math.floor(y);
  const fx = smooth(x - ix), fy = smooth(y - iy);
  const a = lattice(seed, ix, iy), b = lattice(seed, ix + 1, iy);
  const c = lattice(seed, ix, iy + 1), d = lattice(seed, ix + 1, iy + 1);
  return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
}

/** Fractal noise with the LOW frequency dominant, which is the whole point.
 *  `octaves` doubles the frequency and halves the weight each step, so the
 *  first term decides where the highlands are and the last only roughens the
 *  edge. The old generator's two octaves were 1:2.3 in frequency and 0.65:0.35
 *  in weight — near enough to equal that neither could carry a landform. */
export function ridged(seed, x, y, baseWavelength, octaves = 5) {
  let sum = 0, amp = 1, tot = 0, f = 1 / baseWavelength;
  for (let o = 0; o < octaves; o++) {
    sum += vnoise(seed + o * 7919, x * f, y * f) * amp;
    tot += amp;
    amp *= 0.5;
    f *= 2;
  }
  return sum / tot;
}

/* LEVELS ARE PERCENTILES, NOT CONSTANTS.
 *
 * Fixed thresholds on fractal noise do not survive a change of seed: measured
 * with SEA at a constant 0.42, three seeds came out 83.3%, 41.3% and 24.9%
 * water. The noise's own distribution shifts, so the same number means a
 * different world every time and a fifth of them are unplayable.
 *
 * A sea level is the level the sea stands AT. Sort the map and take the tile
 * at the share you want under water, and every world gets the same amount of
 * coast with a different shape — which is the thing that was supposed to vary.
 */
export const SEA_SHARE = 0.30;    // the least sea a world may have
export const SEA_LEVEL = 0.24;    // the height the sea stands at: an island's rim falls below it about at its radius (LS-383)
export const HIGH_SHARE = 0.22;   // ...and about a fifth of it highland
export const CLIFF_SHARE = 0.10;  // the steepest tenth reads as a face of rock
export const TERRACES = 4;        // how many steps the land comes in

/**
 * Build the whole world's terrain fields once.
 * @param {number} seedNum  the world seed
 * @param {number} TW       width in tiles
 * @param {number} TH       height in tiles
 */
/** THE ISLANDS OF A WORLD (LS-383): four to six, as [cx, cy, r] in TILES, the home island first and largest, the
 *  rest a strait apart; an island that cannot be placed a strait from every other is dropped. In tiles, not the
 *  map's normalised frame, so a bigger map holds more islands rather than bigger ones, and they are round. */
export function islandsOf(seedNum: number, TW: number, TH: number): [number, number, number][] {
  const rng = new RNG(mix(seedNum, 3831));
  const count = 4 + rng.int(3), STRAIT = 14, EDGE = 1.1;   // from a sweep over ten seeds: this yields four or five islands on a 256 by 156 map
  const home: [number, number, number] = [TW / 2 + (rng.range(0, 1) - 0.5) * TW * 0.3, TH / 2 + (rng.range(0, 1) - 0.5) * TH * 0.3, 34];
  const out: [number, number, number][] = [home];
  for (let i = 1; i < count; i++) {
    let best: [number, number, number] | null = null, bestGap = -Infinity;
    for (let tries = 0; tries < 300; tries++) {
      const r = 20 + rng.range(0, 1) * 8;
      const c: [number, number, number] = [r * EDGE + rng.range(0, 1) * (TW - 2 * r * EDGE), r * EDGE + rng.range(0, 1) * (TH - 2 * r * EDGE), r];
      const gap = Math.min(...out.map((o) => Math.hypot(c[0] - o[0], c[1] - o[1]) - (c[2] + o[2]) * 1.22));   // land ends at about 1.2 r
      if (gap > bestGap) { bestGap = gap; best = c; }
      if (gap >= STRAIT * 1.5) break;
    }
    if (best && bestGap >= STRAIT) out.push(best);
  }
  return out;
}

export function buildTerrain(seedNum, TW, TH) {
  const isles = islandsOf(seedNum, TW, TH);
  const elev = new Float32Array(TW * TH);
  const moist = new Float32Array(TW * TH);
  const river = new Uint8Array(TW * TH);
  const at = (x, y) => y * TW + x;

  /* ELEVATION, with a dome so the map has edges rather than wrapping.
   * The dome is subtracted, not multiplied: multiplying pulls the whole
   * interior down and gives one central island every time, while subtracting a
   * shallow bowl only bites at the rim. */
  for (let y = 0; y < TH; y++) {
    for (let x = 0; x < TW; x++) {
      const n = ridged(seedNum + 101, x, y, 46, 5);
      /* THE RIM IS A COAST, NOT A PICTURE FRAME.
       *
       * This measured the distance to the edge with `max(|x|, |y|)`, which is
       * the Chebyshev metric — its contours are SQUARES. So the falloff that
       * decides where the sea begins was a square, and the island it cut out
       * was a rectangle with a fringe: an art review's worst finding was that
       * the west coast ran dead straight for 1,200 pixels and the north coast
       * was flat, and this is why.
       *
       * Euclidean distance gives a round island, which is no better on its
       * own — a disc is as artificial as a box. What makes a coastline is that
       * the distance is measured from a WARPED position, so the contour bulges
       * into capes and bites into bays at a scale bigger than any one screen.
       * Two octaves: one at about half a map for the large shape, one at hill
       * scale to rough up the shore. */
      const wx = x + (ridged(seedNum + 7001, x, y, 77, 2) - 0.5) * 46
        + (ridged(seedNum + 7003, x, y, 18, 2) - 0.5) * 14;
      const wy = y + (ridged(seedNum + 7005, x, y, 77, 2) - 0.5) * 46
        + (ridged(seedNum + 7007, x, y, 18, 2) - 0.5) * 14;
      const nx = (wx / (TW - 1)) * 2 - 1, ny = (wy / (TH - 1)) * 2 - 1;
      /* AN ARCHIPELAGO, NOT AN ISLAND (LS-383; Vijay: "change the game world from a single island to an
       * archipelago"). The rim used to be one radial fall from the map's centre; it is the nearest of several
       * now, one per island, so the land stands where any island's fall has not reached and the sea lies between. */
      let fall = Infinity, near = Infinity;
      for (const [cx, cy, r] of isles) { const d = Math.hypot(wx - cx, wy - cy) / r; fall = Math.min(fall, Math.max(0, d - 0.72)); near = Math.min(near, d); }   // in tiles, on the warped position, so the coasts keep the noise's wander
      // ...and past a shore band the noise may not build land at all, so an island ends where its rim says and the
      // strait between two is sea whatever the ridges do (Vijay: "those are too contiguous")
      const shore = Math.max(0, Math.min(1, (near - 0.98) / 0.22));
      elev[at(x, y)] = n * (1 - shore) - fall * 0.9 - shore * 0.35;
      moist[at(x, y)] = ridged(seedNum + 977, x, y, 31, 4);
    }
  }

  /* FILL THE PITS BEFORE TRACING ANYTHING.
   *
   * Raw fractal noise is full of local minima, and a river traced by steepest
   * descent stops dead in the first one it finds: measured, only 13 of 41
   * river ends reached the sea or the map edge and the rest simply stopped in
   * a hollow with nowhere lower to go. On a real landscape those hollows are
   * lakes, and water leaves them over the lowest lip.
   *
   * Priority-flood is the standard answer and it is small: push the whole
   * boundary into a min-heap, always expand the LOWEST tile seen so far, and
   * raise each neighbour to at least that height. A tile can then always walk
   * downhill to the edge, because it was reached from the edge by a path that
   * never went up. `fill` is used for routing only — `elev` keeps its true
   * shape for drawing and for the levels, so filled pits still read as the
   * basins they are and still hold their lakes. */
  const fill = Float32Array.from(elev);
  {
    const heapV: number[] = [], heapI: number[] = [];
    const seen = new Uint8Array(TW * TH);
    const push = (i, v) => {
      heapV.push(v); heapI.push(i);
      let c = heapV.length - 1;
      while (c > 0) {
        const par = (c - 1) >> 1;
        if (heapV[par] <= heapV[c]) break;
        [heapV[par], heapV[c]] = [heapV[c], heapV[par]];
        [heapI[par], heapI[c]] = [heapI[c], heapI[par]];
        c = par;
      }
    };
    const pop = () => {
      const topI = heapI[0], topV = heapV[0];
      const lastV = heapV.pop(), lastI = heapI.pop();
      if (heapV.length) {
        heapV[0] = lastV; heapI[0] = lastI;
        let c = 0;
        for (;;) {
          const l = c * 2 + 1, r = l + 1;
          let m = c;
          if (l < heapV.length && heapV[l] < heapV[m]) m = l;
          if (r < heapV.length && heapV[r] < heapV[m]) m = r;
          if (m === c) break;
          [heapV[m], heapV[c]] = [heapV[c], heapV[m]];
          [heapI[m], heapI[c]] = [heapI[c], heapI[m]];
          c = m;
        }
      }
      return [topI, topV];
    };
    for (let x = 0; x < TW; x++) {
      for (const y of [0, TH - 1]) { seen[at(x, y)] = 1; push(at(x, y), elev[at(x, y)]); }
    }
    for (let y = 1; y < TH - 1; y++) {
      for (const x of [0, TW - 1]) { seen[at(x, y)] = 1; push(at(x, y), elev[at(x, y)]); }
    }
    while (heapV.length) {
      const [i, v] = pop();
      const x = i % TW, y = (i / TW) | 0;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
        const j = at(nx, ny);
        if (seen[j]) continue;
        seen[j] = 1;
        /* +epsilon, WHICH IS THE HALF THAT MAKES IT USEFUL.
         *
         * Filling a pit to the height of its lip makes a FLAT, and steepest
         * descent stalls on a flat exactly as it stalled in the pit: every
         * neighbour ties, nothing is lower, and the trace stops. Measured with
         * a plain fill, 13 of 23 rivers still went nowhere.
         *
         * Raising each tile a hair above the one it was reached FROM gives the
         * whole filled region a gradient back toward the outlet it was filled
         * through — so a river crossing a former pit runs across it and out
         * the lip, which is what a lake with a stream through it does. */
        fill[j] = Math.max(elev[j], v + 1e-5);
        push(j, fill[j]);
      }
    }
  }

  /* THE LEVELS THIS WORLD ACTUALLY HAS. A copy is sorted rather than the
   * field itself, because everything downstream indexes the field by tile. */
  const sorted = Float32Array.from(elev).sort();
  const pick = (share) => sorted[Math.min(sorted.length - 1,
    Math.max(0, Math.floor(share * sorted.length)))];
  /* THE SEA LEVEL IS A HEIGHT, NOT A SHARE (LS-383). A quantile kept a fixed share of the map as land, so islands set
   * apart had the sea sink below their rims until the land spread out to meet: the level is absolute now, and the
   * share is only a floor for a world that came out with too little sea. */
  const sea = Math.max(pick(SEA_SHARE), SEA_LEVEL);
  const high = pick(1 - HIGH_SHARE);
  const slopes = new Float32Array(TW * TH);
  for (let y = 0; y < TH; y++) {
    for (let x = 0; x < TW; x++) {
      const l = elev[at(Math.max(0, x - 1), y)], r = elev[at(Math.min(TW - 1, x + 1), y)];
      const u = elev[at(x, Math.max(0, y - 1))], d = elev[at(x, Math.min(TH - 1, y + 1))];
      slopes[at(x, y)] = Math.hypot(r - l, d - u) / 2;
    }
  }
  const sortedSlope = Float32Array.from(slopes).sort();
  const cliff = sortedSlope[Math.floor((1 - CLIFF_SHARE) * sortedSlope.length)];

  /* RIVERS RUN DOWNHILL. From each source — a high, wet tile — take the
   * steepest descent one tile at a time until the sea, another river, or the
   * map edge. A river drawn as a band never crosses its own contours; a river
   * traced this way lands in the valleys the elevation already has, which is
   * what makes the crossing points obvious to a player without anything
   * marking them. */
  const sources: any[] = [];
  for (let y = 3; y < TH - 3; y++) {
    for (let x = 3; x < TW - 3; x++) {
      const e = elev[at(x, y)];
      if (e < high) continue;
      if (moist[at(x, y)] < 0.45) continue;
      // one source per neighbourhood, so two rivers do not start on one hill
      if ((mix(seedNum, x >> 3, y >> 3, 55) >>> 8) % 11 !== 0) continue;
      sources.push([x, y]);
    }
  }
  for (const [sx, sy] of sources) {
    let x = sx, y = sy;
    for (let step = 0; step < TW + TH; step++) {
      river[at(x, y)] = 1;
      if (elev[at(x, y)] < sea) break;
      /* EIGHT NEIGHBOURS, NOT FOUR.
       *
       * Steepest descent over four neighbours can only ever step N, S, E or W,
       * so a river drawn from it is a staircase of perfect right angles —
       * rendered, the map looked like plumbing laid under the island rather
       * than water finding its way down it. A diagonal step costs sqrt(2) more
       * distance for the same drop, so the gradient is compared per unit
       * travelled and the river takes the genuinely steepest way, which is
       * what makes it meander. */
      let bx = -1, by = -1, best = 0;
      const hEre = fill[at(x, y)];
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1],
        [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) { bx = nx; by = ny; break; }
        /* AND TIES ARE BROKEN BY PLACE, NOT BY ARRAY ORDER.
         *
         * A filled pit is a flat with a uniform epsilon on it, so every
         * direction offers an identical slope and `>` always keeps whichever
         * the loop happened to try first. Rendered, that is a river running
         * dead straight for twenty tiles across a plateau — the one shape
         * water never makes. A hair of positional noise on the comparison
         * costs nothing downhill and lets the channel wander across a flat the
         * way it does everywhere else. */
        const jitter = ((mix(seedNum + 4242, nx, ny) >>> 12) & 255) / 255 * 1e-5;
        const slope = (hEre - fill[at(nx, ny)]) / (dx && dy ? Math.SQRT2 : 1) + jitter;
        if (slope > best) { best = slope; bx = nx; by = ny; }
      }
      if (bx < 0 || bx >= TW || by < 0 || by >= TH) break;
      if (bx === -1) break;
      if (river[at(bx, by)]) { river[at(bx, by)] = 1; break; }
      x = bx; y = by;
    }
  }

  /* TERRACES: THE LAND IN STEPS, WHICH IS WHERE DEPTH COMES FROM.
   *
   * Minish Cap's overworld is layered plateaus, and the reason that reads as a
   * place rather than a picture is that height is a RULE: you can drop off a
   * ledge and you cannot climb it. One field doing three jobs — it is topology
   * you can see over, a shortcut once you know it, and a lock that needs no
   * key and explains itself.
   *
   * It is also the thing this project has failed to build four times. Tiers
   * are BFS distance over a link graph, which is not a shape — measured, tier
   * 1 averages 1.77 spatially separate components, tier 2 3.20 — so a barrier
   * drawn around one seals nothing. A terrace is a level set of a continuous
   * field. It has an inside and an outside because it is made that way.
   *
   * Cut at percentiles like everything else here, so a world always has all of
   * them and only their shape changes. */
  /* CHOSEN SO THE FOUR LEVELS ARE FOUR PLACES, not three and a rumour.
   *
   * These were 0.30/0.62/0.85, which put 15% of the land on the lowest step
   * and left the region tree only two deep — the biggest plateau was the
   * MIDDLE shelf, so walking outward to the shore and inward to the peak were
   * both one step, and the nesting the tiers are read from collapsed. Swept
   * against land share per tier: these give 31/28/27/15 and a tree three deep
   * on every seed tried, so the world opens out in four stages of decreasing
   * size instead of one large one and a scrap. */
  const STEPS = [0.47, 0.70, 0.88];        // shares below each cut
  /* ...OVER THE LAND (LS-383). The shares were taken over the whole map; with near half of it sea, the sea took the
   * low quantiles and every island collapsed into the top steps -- the start region became a coastal strip. The
   * cuts are quantiles of the land's own heights, and the sea floor is the lowest step regardless. */
  const landSorted = sorted.filter((v) => v >= sea);
  const pickLand = (share) => landSorted.length ? landSorted[Math.min(landSorted.length - 1, Math.max(0, Math.floor(share * landSorted.length)))] : pick(share);
  const cuts = STEPS.map((q) => pickLand(q));
  const terrace = new Uint8Array(TW * TH);
  for (let k = 0; k < TW * TH; k++) {
    let n = 0;
    while (n < cuts.length && elev[k] >= cuts[n]) n++;
    terrace[k] = n;                        // 0 lowest .. 3 highest
  }

  /* AND THEN THE PLATEAUS ARE CLEANED, which is what makes this a game's
   * heightmap rather than a topographic map.
   *
   * Slicing a smooth field at three levels gives CONTOURS — bands that wander
   * a tile at a time and are a tile or two wide wherever the ground is nearly
   * flat. Drawn as cliffs those become two-pixel stubs scattered over the
   * ground, which is exactly how they looked: litter, not terrain.
   *
   * Minish Cap's overworld is not a contour map. It is a handful of flat
   * plateaus with long hard edges and a way up at chosen points, and the
   * player has to be able to see the shape of a level to plan around it.
   *
   * A majority filter is the standard cleanup: each tile takes the commonest
   * level among itself and its neighbours. Run twice, it eats single-tile
   * speckle and one-tile-wide fingers, straightens ragged boundaries and
   * leaves the large plateaus exactly where they were. */
  for (let pass = 0; pass < 2; pass++) {
    const src = Uint8Array.from(terrace);
    for (let y = 0; y < TH; y++) {
      for (let x = 0; x < TW; x++) {
        const tally = [0, 0, 0, 0];
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const nx = Math.max(0, Math.min(TW - 1, x + dx));
            const ny = Math.max(0, Math.min(TH - 1, y + dy));
            tally[src[at(nx, ny)]]++;
          }
        }
        let bestLvl = src[at(x, y)];
        for (let l = 0; l < 4; l++) {
          if (tally[l] > tally[bestLvl]) bestLvl = l;
        }
        terrace[at(x, y)] = bestLvl;
      }
    }
  }

  /* AND THIN TERRACES ARE COLLAPSED, WHICH IS WHERE TALL CLIFFS COME FROM.
   *
   * Slicing a smooth field at fixed heights gives bands, and two neighbouring
   * tiles almost never fall two bands apart — measured over four worlds, 4,052
   * of 4,054 steps were exactly one level, so every cliff in the world was one
   * tile tall and the whole map read as a single storey with lines drawn on
   * it. Real ground does not do that: a hillside has a low step here and a
   * thirty-foot face there.
   *
   * A band only a tile or two wide is not a terrace anybody can stand on — it
   * is the middle of a slope, drawn as a shelf. Snapping those to the level
   * above merges two boundaries into one, and the step that results is TWO
   * levels tall. Do it twice and three-level faces appear where the ground is
   * steepest, which is exactly where they belong. */
  for (let pass = 0; pass < 2; pass++) {
    const src = Uint8Array.from(terrace);
    for (let y = 0; y < TH; y++) {
      for (let x = 0; x < TW; x++) {
        const j = at(x, y);
        const lv = src[j];
        let same = 0, higher = 0, lower = 0;
        for (let dy = -2; dy <= 2; dy++) {
          for (let dx = -2; dx <= 2; dx++) {
            const nx = Math.max(0, Math.min(TW - 1, x + dx));
            const ny = Math.max(0, Math.min(TH - 1, y + dy));
            const v = src[at(nx, ny)];
            if (v === lv) same++; else if (v > lv) higher++; else lower++;
          }
        }
        // a shelf you could not stand on, with ground above AND below it
        if (same < 13 && higher > 0 && lower > 0) {
          terrace[j] = higher >= lower ? Math.min(TERRACES - 1, lv + 1) : Math.max(0, lv - 1);
        }
      }
    }
  }

  /* THE TERRACES ARE CUT IN THREE-TILE BLOCKS.
   *
   * Only a rock tile with higher ground to its NORTH is drawn as a wall; the
   * sides and back of a plateau are its own floor with a line of ink
   * (`drawCliffs`). The boundaries the height field gives are stair-stepped
   * at one tile, and on a staircase every front is one tile wide: measured
   * over three worlds, 69% of front tiles sat in runs one or two wide, which
   * once the sides went flat were lone dark cubes standing on open ground.
   *
   * So the field is quantised: each three-by-three block takes the level
   * most of its land tiles hold, and every edge comes out axis-aligned in
   * runs of three. Simulated before it was built -- front tiles in faces of
   * three or more: block 1, 260; block 2, 384 (half still stubs); block 3,
   * 764 and none narrower. The chunky terrace of a 16-bit overworld is not a
   * limitation of its hardware; it is what makes its cliffs read. */
  blockTerraces(terrace, fill, sea, TW, TH, at, seedNum);

  /* HOW TALL EACH CLIFF IS, WHICH IS NOT HOW MANY TERRACES IT CROSSES.
   *
   * Every step in the world was one terrace and therefore one tile — measured,
   * 3,782 drops of one level against 151 of two — so every cliff was 16 pixels
   * high and the map read as a single storey with lines ruled on it. Cutting
   * more terraces does give taller steps (six levels gave 668 double drops),
   * but it also shatters the world into 151 plateaus, and the plateaus are
   * what the tiering is read from.
   *
   * So the two uses are separated. The terraces stay at four and keep their
   * job, which is deciding where the walls are. How TALL a wall is comes from
   * the elevation actually lost across it, measured against the height of one
   * band — a step down a gentle shoulder is one tile, one off a headland is
   * three. Same walls, varied faces.
   *
   * `face[j]` is 0 for ordinary ground, and otherwise the number of tiles this
   * cliff occupies counting down from its brink. Everything that asks "is this
   * a cliff" asks this array, so the collision, the regions and the picture
   * cannot disagree about where the rock is. */
  const face = new Uint8Array(TW * TH);
  {
    /* HOW FAR THE GROUND KEEPS RISING is the height of the wall.
     *
     * The first cut of this took the elevation difference between the two
     * ADJACENT tiles, which is the local gradient and is a hundredth of a band
     * — so every face rounded to one tile and nothing changed. A terrace step
     * is a threshold crossing, not a jump: what makes a cliff look tall is
     * that the ground above it goes on climbing.
     *
     * So look three tiles into the high side. If the land gains another
     * terrace within that distance the face is two tiles of rock; if it gains
     * two, three. A shoulder stays a single step and a headland becomes a
     * wall, which is the variation the map was missing. */
    for (let y = 0; y < TH; y++) {
      for (let x = 0; x < TW; x++) {
        const j = at(x, y);
        if (fill[j] < sea) continue;
        let hgt = 0;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
          const k = at(nx, ny);
          if (fill[k] < sea || terrace[k] <= terrace[j]) continue;
          hgt = Math.max(hgt, 1);
          for (let step = 2; step <= 3; step++) {
            const ux = x + dx * step, uy = y + dy * step;
            if (ux < 0 || uy < 0 || ux >= TW || uy >= TH) break;
            const u = at(ux, uy);
            if (fill[u] < sea) break;
            hgt = Math.max(hgt, Math.min(3, terrace[u] - terrace[j]));
          }
        }
        if (hgt > 0) face[j] = hgt;
      }
    }
    /* AND IT TAKES NO EXTRA GROUND.
     *
     * A first version claimed the tiles below a tall face as rock, on the
     * reasoning that two tiles of wall is two tiles of wall. It is, in a
     * side-on game; here it moved the walls, which moved the plateaus, which
     * moved the tiering — the gating proof went from 4 failures in 201 seeds
     * to 16 in 61 for a 4% gain in tall faces.
     *
     * Height is a matter for the PICTURE. `face[j]` stays one tile of rock
     * with a number on it, and the draw hangs the face down over the ground in
     * front of it, which is what a cliff looks like from above anyway. The
     * collision, the regions and the tiering never hear about it. */
  }

  /* `crag` IS HUNG ON THIS OBJECT LATER, and the type has to say so or every reader is an error. `buildTerrain`
   * sets `t.crag` after the fact -- deliberately, per the note there: `buildRegions` already reaches in and
   * rewrites `t.face`, so the terrain is the one place to ask about rock. Four readers in `game.ts` and `hud.ts`
   * guard on it and were errors against a type that had never been told (2026-09-06). */
  /* NO TERRACES UNDER THE SEA (LS-383). The steps kept running below the water, and the relief pass drew their
   * edges as contour lines across every strait once the sea was more than a rim. The sea floor is the lowest
   * step, flat. */
  for (let k = 0; k < TW * TH; k++) if (elev[k] < sea) terrace[k] = 0;
  return { elev, fill, moist, river, terrace, face, cuts, TW, TH, at, sea, high, cliff,
    crag: undefined as any,
    blockSeed: seedNum };   // the brick cut is seeded by the world (see blockTerraces)
}

/** How steep it is here — the cliff test, and the thing that makes a pass. */
export function slopeAt(t, x, y) {
  const { elev, at, TW, TH } = t;
  const l = elev[at(Math.max(0, x - 1), y)], r = elev[at(Math.min(TW - 1, x + 1), y)];
  const u = elev[at(x, Math.max(0, y - 1))], d = elev[at(x, Math.min(TH - 1, y + 1))];
  return Math.hypot(r - l, d - u) / 2;
}

/* THE WORLD'S REGIONS, WHICH ARE WHERE GATING BELONGS.
 *
 * Tiering was BFS over a graph of SCREEN PAIRS, and that graph is only
 * meaningful if every screen is walled — measured, derive the links from the
 * terrain instead and 106 to 111 of the 112 adjacent pairs are joined, because
 * one walkable tile anywhere along a twelve- or sixteen-tile seam links the
 * pair. So the screen graph cannot carry a barrier, the border wall exists to
 * make it carry one, and the wall is why the world reads as a lattice.
 *
 * A terrace, though, does separate: with steps impassable the land falls into
 * eight to fifteen plateaus, the largest holding 39% of it. Those are regions
 * a barrier can stand at the edge of, and the place it stands is a RAMP — the
 * one tile where two plateaus touch and the step is climbable. That is the
 * Link to the Past arrangement stated plainly: the cliff is the wall and the
 * ramp is the door.
 *
 * Returns the region id per tile (-1 for sea), each region's size and centre,
 * and every ramp candidate as the pair it joins plus the tile it sits on. */
export const BLOCK = 3;
/* THE TERRACES ARE CUT IN BRICKS, NOT ON A GRID.
 *
 * A uniform three-by-three cut made every ledge run straight and turn at a
 * right angle on the same lines everywhere, and the map read as a lattice:
 * "why is the map still a lattice". What the cut is FOR is the fronts -- a
 * face narrower than three tiles is a stub -- and only the south edge of a
 * plateau is a front. So the cells are bricks: bands two to four tiles
 * tall, each band cut into cells three to six wide, each band's cuts
 * shifted by its own offset so no vertical edge lines up with the band
 * above or below. Every cell is at least three wide, so a plateau's south
 * edge -- whole cells -- is never narrower than three; the sides and backs
 * are free to wander. Seeded by the world, so a world is itself. */
/** THE CELLS THE TERRACES ARE CUT IN, as one list: `[x0, x1, y0, y1)` per cell, bands from the top. Written out
 *  because TWO passes need the same cells and only one of them had them (2026-09-17). The sliver raise keyed its
 *  work on a uniform three-by-three grid while these bands are two to four rows tall and three to nine wide, so
 *  207 of 312 raised blocks straddled a band boundary: the cell each landed in saw a minority of its tiles raised
 *  and voted them all back. Fifty-one small regions over six seeds were raised and put back, every pass. */
export function terraceCells(TW: number, TH: number, seed = 0): Array<[number, number, number, number]> {
  const h32 = (a: number, b: number) => {
    let x = (a * 374761393 + b * 668265263 + seed * 2246822519) | 0;
    x = (x ^ (x >>> 13)) * 1274126177 | 0;
    return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
  };
  const out: Array<[number, number, number, number]> = [];
  let by = 0, band = 0;
  while (by < TH) {
    const bh = 2 + Math.floor(h32(band, 1) * 3);                 // 2..4 tall
    /* EVERY VERTICAL EDGE ON THE THREE-GRID. A front is the overlap of a cell in this band with a differently-
     * levelled cell in the band above, and free offsets left overlaps of one or two: 116 stubs in 252 fronts.
     * Widths of three, six or nine and offsets of nought, three or six put every boundary on the same three-grid,
     * so no overlap is under three -- and the edges still stagger, band to band. */
    const shift = 3 * Math.floor(h32(band, 2) * 3);              // 0, 3 or 6
    let bx = -shift, cell = 0;
    while (bx < TW) {
      const bw = 3 * (1 + Math.floor(h32(band, 100 + cell) * 3));  // 3, 6 or 9 wide
      out.push([Math.max(0, bx), Math.min(TW, bx + bw), by, Math.min(TH, by + bh)]);
      bx += bw; cell++;
    }
    by += bh; band++;
  }
  return out;
}
export function blockTerraces(terrace, fill, sea, TW, TH, at, seed = 0) {
  for (const [x0, x1, y0, y1] of terraceCells(TW, TH, seed)) {
    /* THE SEA VOTES TOO, AND THAT IS WHY A COASTAL SHELF IS STILL VOTED BACK (2026-09-17, left as it is on purpose).
     * A sea tile has no terrace of its own and always reads level 0, so a cell that is mostly water holds its land
     * at 0 whatever the land is -- which is the other half of why the sliver loop does not always converge: 94
     * regions under its own minimum survive across twelve worlds. Counting the land alone was built and MEASURED
     * (19 regions left instead of 94, and the picture reads better) and then taken out again, because it moves
     * every coast in every world: on one seed the home island lost its only brazier, which the found forge needs,
     * and one seam grew a ruled brightness line the seam check caught. It is a world change with fixtures of its
     * own to re-pin, not a line to slip into a bug batch. */
    const cnt = [0, 0, 0, 0];
    for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) cnt[terrace[at(x, y)]]++;
    let m = 0;
    for (let l = 1; l < cnt.length; l++) if (cnt[l] > cnt[m]) m = l;
    if (cnt[m] > 0) for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) terrace[at(x, y)] = m;
  }
}
/** THE COAST FIELD: for every tile, its distance to the shore -- positive on land (to the nearest water), negative in
 *  water (to the nearest land) -- by a two-pass chamfer sweep. `wet(j)` says which tiles are water; the world asks
 *  again after the seal drowns islets (LS-383), because a drowned tile is sea to every painter and must be sea here. */
export function coastField(TW: number, TH: number, at: (x: number, y: number) => number, wet: (j: number) => boolean): Float32Array {
  const coast = new Float32Array(TW * TH);
  const BIG = 1e5;
  const dLand = new Float32Array(TW * TH);
  const dWater = new Float32Array(TW * TH);
  for (let j = 0; j < TW * TH; j++) { const w = wet(j); dLand[j] = w ? BIG : 0; dWater[j] = w ? 0 : BIG; }
  const sweep = (d) => {
    const step = (i, k, c) => { if (d[k] + c < d[i]) d[i] = d[k] + c; };
    for (let y = 0; y < TH; y++) {
      for (let x = 0; x < TW; x++) {
        const i = at(x, y);
        if (x > 0) step(i, i - 1, 1);
        if (y > 0) step(i, i - TW, 1);
        if (x > 0 && y > 0) step(i, i - TW - 1, 1.41421356);
        if (x < TW - 1 && y > 0) step(i, i - TW + 1, 1.41421356);
      }
    }
    for (let y = TH - 1; y >= 0; y--) {
      for (let x = TW - 1; x >= 0; x--) {
        const i = at(x, y);
        if (x < TW - 1) step(i, i + 1, 1);
        if (y < TH - 1) step(i, i + TW, 1);
        if (x < TW - 1 && y < TH - 1) step(i, i + TW + 1, 1.41421356);
        if (x > 0 && y < TH - 1) step(i, i + TW - 1, 1.41421356);
      }
    }
  };
  sweep(dLand); sweep(dWater);
  for (let j = 0; j < TW * TH; j++) coast[j] = wet(j) ? -dLand[j] : dWater[j];
  return coast;
}

export function buildRegions(t) {
  const { fill, terrace, elev, at, TW, TH, sea } = t;
  const land = (x, y) => fill[at(x, y)] >= sea;

  /* A REGION IS GROUND YOU CAN WALK, and the cliffs are what lie between.
   *
   * The first version made a region "land at the same terrace" and left the
   * cliff FEET inside it — and a foot is solid, so a plateau's own edge became
   * a one-tile wall running through the middle of its own region. Where two
   * such walls came close they pinched it in half: measured on seed-8, the
   * player reached 151 tiles of the world from a spawn that the region graph
   * said was on a plateau of three thousand. No route could cut it either,
   * because cutting a foot opens the tier above it.
   *
   * So the feet belong to NO region. What is left is exactly the ground a
   * player can stand on and walk between without climbing, which makes region
   * connectivity and tile connectivity the same fact — and it makes a ramp a
   * very simple thing: one foot tile, with a different region on either side
   * of it, opened. */
  /* WHICH WATER IS THE OPEN SEA. Flood inward from the rim over everything
   * below sea level: what that reaches is the sea — the edge of the world,
   * which no charm opens. What it does not reach is a pond, and the tide is
   * meant to open those. */
  const isSea = new Uint8Array(TW * TH);
  {
    const q: any[] = [];
    const push = (x, y) => {
      const j = at(x, y);
      if (!isSea[j] && !land(x, y)) { isSea[j] = 1; q.push([x, y]); }
    };
    for (let x = 0; x < TW; x++) { push(x, 0); push(x, TH - 1); }
    for (let y = 0; y < TH; y++) { push(0, y); push(TW - 1, y); }
    for (let i = 0; i < q.length; i++) {
      const [cx, cy] = q[i];
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = cx + dx, ny = cy + dy;
        if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
        push(nx, ny);
      }
    }
  }

  /* HOW FAR IS THE SEA FROM HERE — one field, everything samples it.
   *
   * Borrowed from the sibling project's `worldfield.ts`, which builds a signed
   * distance field over its terrain bitfield by chamfer transform and has
   * every system query THAT rather than growing its own point probes. Its
   * comment names the failure it was written to end: "ad-hoc probe offsets
   * disagreeing with each other", which is the same class of bug this file has
   * hit repeatedly — the tile pass working out where the rock is while the
   * regions worked it out again differently.
   *
   * Two passes, forward then backward, taking the cheaper of a straight step
   * (1) and a diagonal (1.41). That is exact enough at tile resolution and it
   * costs one sweep each way over the map.
   *
   * `coast[j]` is distance IN TILES to the nearest tile of the other kind:
   * positive on land (how far to water), negative in water (how far to land).
   * A coastline can then be as wide as it needs to be — a shelf of shallows,
   * a line of foam, a strip of wet sand — instead of the one-tile contour that
   * an elevation threshold gives. */
  const coast = coastField(TW, TH, at, (j) => fill[j] < sea);

  /* COMPUTED, NOT LOOKED UP. `buildTerrain` fills a `face` array, but the
   * sliver merges below rewrite the terrace and every merge moves the feet —
   * so reading that array here is reading the terrain as it was before this
   * function started changing it. The gating proof went from 4 failures in 201
   * seeds to 9 in 61 on exactly that. `face` is refreshed at the end, once the
   * terrace has stopped moving. */
  /** Overridden once the face heights are known, so the last labelling sees
   *  the tall walls as the rock they are drawn as. */
  let footOverride: any = null;
  const isFoot = (x, y) => {
    if (footOverride) return footOverride[at(x, y)] > 0;
    const lv = terrace[at(x, y)];
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = x + dx, ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
      if (land(nx, ny) && terrace[at(nx, ny)] > lv) return true;
    }
    return false;
  };

  const label = () => {
    const id = new Int32Array(TW * TH).fill(-1);
    const foot = new Uint8Array(TW * TH);
    for (let y = 0; y < TH; y++) {
      for (let x = 0; x < TW; x++) if (land(x, y) && isFoot(x, y)) foot[at(x, y)] = 1;
    }
    const regions: any[] = [];
    /* ONE STACK FOR THE WHOLE PASS (2026-09-06). The typed stack below replaced
     * a two-element array per step, and then allocated a fresh TW*TH Int32Array
     * for EVERY REGION -- and `label` is called up to sixty-five times a
     * terrain by the sliver loop beneath it, which is why the garbage collector
     * was showing up in the profile at all. It is a stack: `sp = 0` empties it.
     * The four neighbours are unrolled for the same reason, since the literal
     * built four arrays and an iterator on every tile popped. */
    const stack = new Int32Array(TW * TH);
    for (let y0 = 0; y0 < TH; y0++) {
      for (let x0 = 0; x0 < TW; x0++) {
        const j0 = at(x0, y0);
        if (id[j0] >= 0 || !land(x0, y0) || foot[j0]) continue;
        const r = regions.length;
        let sp = 0; stack[sp++] = j0;
        id[j0] = r;
        let n = 0, sx = 0, sy = 0;
        while (sp > 0) {
          const j = stack[--sp];
          const cx = j % TW, cy = (j - cx) / TW;
          n++; sx += cx; sy += cy;
          let k = j + 1;
          if (cx + 1 < TW && id[k] < 0 && !foot[k] && land(cx + 1, cy)) { id[k] = r; stack[sp++] = k; }
          k = j - 1;
          if (cx > 0 && id[k] < 0 && !foot[k] && land(cx - 1, cy)) { id[k] = r; stack[sp++] = k; }
          k = j + TW;
          if (cy + 1 < TH && id[k] < 0 && !foot[k] && land(cx, cy + 1)) { id[k] = r; stack[sp++] = k; }
          k = j - TW;
          if (cy > 0 && id[k] < 0 && !foot[k] && land(cx, cy - 1)) { id[k] = r; stack[sp++] = k; }
        }
        regions.push({ r, n, cx: Math.round(sx / n), cy: Math.round(sy / n), lvl: terrace[j0] });
      }
    }
    /* Every foot with two different regions around it is a place a ramp could
     * go: cut that one tile and the two plateaus join. */
    const pairs = new Map();
    /* A PAIR IS FOUND THROUGH THE WALL, NOT ACROSS ONE TILE OF IT.
     *
     * Two regions were paired only where a single rock tile had both as
     * orthogonal neighbours -- a wall exactly one tile thick. The claim pass
     * stacks rock two to five tiles deep under every drawn face, and measured
     * over seven worlds 35% of wall sightings are thicker than one tile
     * (1: 11258, 2: 2698, 3: 1292, 4: 600, 5: 410, then a tail); every one of
     * those had no pair, so no ramp, so a strip of low ground beneath it that
     * nothing could reach -- wide-0, wide-27 and wide-29 each ended in one.
     *
     * So from every rock tile with open ground behind it, walk on through the
     * rock until open ground of another region, and hand the ramp pass the
     * whole run as its corridor. Five covers 96% of the sightings; beyond
     * that the seal fords or cuts. */
    const CORRIDOR_MAX = 5;
    for (let y = 0; y < TH; y++) {
      for (let x = 0; x < TW; x++) {
        const j = at(x, y);
        if (!foot[j]) continue;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const bx = x - dx, by = y - dy;
          if (bx < 0 || by < 0 || bx >= TW || by >= TH) continue;
          const ra = id[at(bx, by)];
          if (ra < 0) continue;
          let n = 1;
          while (n < CORRIDOR_MAX) {
            const nx = x + dx * n, ny = y + dy * n;
            if (nx < 0 || ny < 0 || nx >= TW || ny >= TH || !foot[at(nx, ny)]) break;
            n++;
          }
          if (n < 2) continue;                       // one thick: the scan below has it
          const ex = x + dx * n, ey = y + dy * n;
          if (ex < 0 || ey < 0 || ex >= TW || ey >= TH) continue;
          const rb = id[at(ex, ey)];
          if (rb < 0 || rb === ra) continue;
          const lo = Math.min(ra, rb), hi = Math.max(ra, rb);
          const key = lo + '|' + hi;
          let p = pairs.get(key);
          if (!p) { p = { a: lo, b: hi, tiles: [] }; pairs.set(key, p); }
          p.tiles.push([x, y, x + dx * (n - 1), y + dy * (n - 1)]);
        }
        const near = new Set<number>();
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nx = x + dx, ny = y + dy;
          if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
          const r = id[at(nx, ny)];
          if (r >= 0) near.add(r);
        }
        const list = [...near];
        for (let a = 0; a < list.length; a++) {
          for (let b = a + 1; b < list.length; b++) {
            const lo = Math.min(list[a], list[b]), hi = Math.max(list[a], list[b]);
            const key = lo + '|' + hi;
            let p = pairs.get(key);
            if (!p) { p = { a: lo, b: hi, tiles: [] }; pairs.set(key, p); }
            // the foot itself, and which side is which, for the corridor
            p.tiles.push([x, y, x, y]);
          }
        }
      }
    }
    return { id, regions, pairs, foot };
  };

  /* A POND MAY NOT JOIN TWO PLATEAUS.
   *
   * A cliff stops you climbing; water does not, once you hold the tide. So a
   * pond lying between two regions is a way from one to the other for whoever
   * holds that one charm, whatever the ramps between them cost — every cliff
   * in the neighbourhood bypassed. Traced twice on the gating proof:
   * `GROUND(t1) -> WATER -> SAND(t2)` on seed-9, and the same shape on seed-11
   * through a bramble the thicket pass grows in the shallows, which ember
   * burns.
   *
   * THE TEST IS REGIONS, NOT TERRACES, and getting that wrong cost a round:
   * regions are walkable ground now, so two of them can sit at the SAME
   * terrace and still be different places with different tiers. A pond with
   * two regions on its banks is filled to the level of the lower one. */
  const fillPonds = (out) => {
    const seen = new Uint8Array(TW * TH);
    let filled = 0;
    for (let y0 = 0; y0 < TH; y0++) {
      for (let x0 = 0; x0 < TW; x0++) {
        const j0 = at(x0, y0);
        if (seen[j0] || isSea[j0] || land(x0, y0)) continue;
        const body: number[] = [];
        const banks = new Set<number>();
        const lvls = new Set<number>();
        const q: any[] = [[x0, y0]];
        seen[j0] = 1;
        while (q.length) {
          const [cx, cy] = q.pop();
          body.push(at(cx, cy));
          for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const nx = cx + dx, ny = cy + dy;
            if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
            const k = at(nx, ny);
            if (land(nx, ny)) {
              lvls.add(terrace[k]);
              if (out.id[k] >= 0) banks.add(out.id[k]);
              continue;
            }
            if (seen[k] || isSea[k]) continue;
            seen[k] = 1; q.push([nx, ny]);
          }
        }
        if (banks.size <= 1) continue;
        const low = Math.min(...lvls);
        for (const k of body) { fill[k] = sea; terrace[k] = low; }
        filled++;
      }
    }
    return filled;
  };

  /* A PLATEAU TOO SMALL TO STAND ON IS NOT A PLATEAU. Flatten it into whichever
   * neighbour it shares the most edge with, then label again — the flatten
   * changes the terrace, which changes where the feet are. */
  const MIN = 40;
  let out = label();
  for (let guard = 0; guard < 64; guard++) {
    if (fillPonds(out)) { out = label(); continue; }
    let worst = -1;
    for (const r of out.regions) {
      if (r.n < MIN && (worst < 0 || r.n < out.regions[worst].n)) worst = r.r;
    }
    if (worst < 0) break;
    const contact = new Map();
    for (const p of out.pairs.values()) {
      if (p.a === worst) contact.set(p.b, (contact.get(p.b) || 0) + p.tiles.length);
      if (p.b === worst) contact.set(p.a, (contact.get(p.a) || 0) + p.tiles.length);
    }
    let into = -1, best = -1;
    for (const [r, n] of contact) if (n > best) { best = n; into = r; }
    if (into < 0) break;
    const lvl = out.regions[into].lvl;
    for (let j = 0; j < out.id.length; j++) if (out.id[j] === worst) terrace[j] = lvl;
    // the absorbed sliver was a region, not a block; seed-92 kept one stub at 70,59
    blockTerraces(terrace, fill, sea, TW, TH, at, t.blockSeed || 0);
    out = label();
  }
  /* ...and now the terrace has settled, say how tall each face is. Three
   * tiles into the high side: a shoulder that keeps climbing is a wall, a
   * shelf that levels off is a step. Rendering only — nothing above reads it. */
  const face = t.face;
  let crag;
  /* THE FACES ARE CARVED BY A FUNCTION, BECAUSE THEY ARE CARVED MORE THAN ONCE.
   * The small-region pass below used to turn any open region under MIN tiles
   * into rock. With the terraces cut in three-tile blocks a one-block shelf is
   * nine tiles, the claim under the face above it takes a row or two, and what
   * is left is under MIN: the shelf became a slab and the wall behind it ran
   * nine tiles deep (wide-29: six-thick walls, 492 sightings over seven
   * worlds). A shelf too small to stand on is RAISED to the level of the
   * region it touches most instead, and the faces are cut again. */
  const carve = () => {
  face.fill(0);
  const climbOf = new Map();
  for (let y = 0; y < TH; y++) {
    for (let x = 0; x < TW; x++) {
      const j = at(x, y);
      if (!land(x, y)) continue;
      /* HEIGHT IS HOW FAR THE LAND CLIMBS, measured in elevation rather than
       * in terraces. Counting terrace levels within three tiles gave 178 tall
       * faces out of 3,900 — four per cent, which is not variety, it is an
       * exception. The terraces are four nested rings, so gaining a second one
       * within three tiles happens only where a band is unusually narrow.
       *
       * The ground itself has no such quantisation. Taking the elevation
       * climbed over five tiles against the height of one band gives a face
       * that is tall where the hillside is steep and low where it is gentle,
       * which is the thing being asked for. */
      let hgt = 0, climb = 0;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
        if (!land(nx, ny) || terrace[at(nx, ny)] <= terrace[j]) continue;
        hgt = 1;
        for (let step = 1; step <= 5; step++) {
          const ux = x + dx * step, uy = y + dy * step;
          if (ux < 0 || uy < 0 || ux >= TW || uy >= TH) break;
          if (!land(ux, uy)) break;
          climb = Math.max(climb, elev[at(ux, uy)] - elev[j]);
        }
      }
      face[j] = hgt;                 // 1 for now; the height is set below
      if (hgt) climbOf.set(j, climb);
    }
  }
  /* --- A WALL TOO SHORT TO BE A WALL IS DRAWN AS A STEP, NOT DELETED.
   *
   * A cliff foot is decided per tile, so a one-tile step in the terrace field
   * becomes a one-tile cliff — and drawn at full height that is a short dark
   * bar with a lit top edge standing in open ground, bounding nothing.
   * Measured over three worlds, 66-77% of the CLIFF formations on the finished
   * map were three tiles or fewer, median one or two; an art critic reading a
   * render of the whole island ranked it third of seventeen at 0.9 confidence
   * and called them fallen logs scattered on flat ground.
   *
   * THE FIRST FIX WAS TO CLEAR THEM AND IT DESTROYED THE GAME. Lifting every
   * face component under four tiles took gate.ts from 301/301 to 1,067
   * failures on 298 seeds, reporting `empty tier 1/0/0/0`: those fragments ARE
   * the walls between plateaus. The confetti and the gating structure are the
   * same tiles, and the picture cannot be fixed by removing them.
   *
   * What can change is how TALL they are drawn. Height is paint — the height
   * loop below claims it as rock, and `drawCliffs` hangs a face that many tiles
   * deep — so capping a small formation at one keeps the foot exactly where it
   * was, keeps `face > 0` and so keeps the region labelling and every tier
   * derived from it, and turns a three-tile bar lying in a meadow into the kerb
   * it actually is. */
  /* AND A FORMATION THAT SMALL IS MARKED, because a kerb is not a short wall.
   *
   * Capping the height fixed how TALL these are drawn and left them drawn as
   * WALLS: a one-tile face is a filled rectangle with a lit top edge and an ink
   * outline, so a lone fragment came out as a plank lying in the grass, and the
   * same critique came back a third time from play -- "how have we still not
   * fixed this" -- over a screen of scree where every blocker was a grey slab.
   *
   * The picture wants them to be OUTCROPS: boulders shouldering out of the
   * ground, which is what a one-tile lump of rock in a field is, and which the
   * art already draws convincingly for T.ROCK. That is a question about the
   * SHAPE of the formation, not about any one tile, so it is answered here
   * where the components are already walked and carried to the draw. */
  crag = new Uint8Array(TW * TH);
  {
    const seenF = new Uint8Array(TW * TH);
    const MINFACE = 4;
    /* EIGHT-CONNECTED, AND THIS WAS THE WHOLE BUG.
     *
     * A terrace boundary that runs diagonally is a STAIRCASE of feet, and the
     * tiles of a staircase touch only at their corners. Walked with the four
     * orthogonal neighbours, every one of them is a component of ONE, so a
     * contour four screens long was read as a hundred separate lumps and every
     * one of them was capped at a single tile of height and later drawn as a
     * boulder. That is why an island whose terrace map shows four large clean
     * plateaus had nine three-tile faces on it, and it is what a false-colour
     * render of `face` showed in one look: the cliffs were drawn almost
     * entirely in the colour reserved for "too small to be a wall".
     *
     * A wall is a wall around its corners. */
    const NBR = [[1, 0], [-1, 0], [0, 1], [0, -1],
      [1, 1], [1, -1], [-1, 1], [-1, -1]];
    for (let y = 0; y < TH; y++) {
      for (let x = 0; x < TW; x++) {
        const j = at(x, y);
        if (seenF[j] || !face[j]) continue;
        const body = [];
        const q = [[x, y]];
        seenF[j] = 1;
        while (q.length) {
          const [cx, cy] = q.pop();
          body.push(at(cx, cy));
          for (const [dx, dy] of NBR) {
            const nx = cx + dx, ny = cy + dy;
            if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
            const k = at(nx, ny);
            if (seenF[k] || !face[k]) continue;
            seenF[k] = 1;
            q.push([nx, ny]);
          }
        }
        if (body.length < MINFACE) {
          for (const k of body) { face[k] = 1; crag[k] = 1; }
        }
      }
    }
    /* Hung on the terrain, not returned: `buildRegions` already reaches into
     * `t.face` and rewrites it, and the draw asks the terrain for everything
     * else it knows about the rock. One place to ask. */
    t.crag = crag;
    /* AND HOW LONG EACH WALL ACTUALLY RUNS (LS-199). Reported from play with
     * a screenshot and four arrows on it: "what are these random ledges, they
     * really mess with my sense of depth perception". Every arrow was on a
     * STUB -- one, two or three tiles of visible face standing in open dirt.
     * A stub of masonry lying in a field is a crate: its lit brink reads as a
     * top face, so it promises a step onto a level that is not there.
     *
     * `crag` catches small FORMATIONS; this catches short VISIBLE RUNS, which
     * is a different question and the one the arrows were asking. Computed on
     * demand and cached, because the face heights are still being settled at
     * this point and the run only depends on which tiles show a face at all.
     * Both the tile art (`hud.ts` sets a bit) and the live wall pass
     * (`game.ts` skips the span) ask this one function, so the picture cannot
     * disagree with itself: without that, the tile drew a rock and the wall
     * pass hung masonry over the top of it. */
    let stubs: Uint8Array | null = null;
    t.stubAt = (x: number, y: number) => {
      if (!stubs) {
        stubs = new Uint8Array(TW * TH);
        const shows = (nx: number, ny: number) => {
          if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) return false;
          const k = at(nx, ny);
          return face[k] > 0 && terrace[at(nx, Math.max(0, ny - 1))] > terrace[k];
        };
        for (let yy = 0; yy < TH; yy++) {
          let run = 0;
          for (let xx = 0; xx <= TW; xx++) {
            if (xx < TW && shows(xx, yy)) { run++; continue; }
            if (run > 0 && run <= 3) for (let k = xx - run; k < xx; k++) stubs[at(k, yy)] = 1;
            run = 0;
          }
        }
      }
      if (x < 0 || y < 0 || x >= TW || y >= TH) return false;
      return !!stubs[at(x, y)];
    };
  }

  /* THE HEIGHTS ARE CUT BY PERCENTILE, not against an absolute.
   *
   * Two absolute rules were tried and both gave a world of uniform one-tile
   * steps: counting terrace levels within three tiles produced 4% tall faces,
   * and measuring elevation climbed against the height of a band produced 1%.
   * The reason is the same each time — this island is gentle, so a fixed
   * threshold in world units is almost never met, and the answer saturates.
   *
   * Every other cut in this file is a percentile for exactly that reason, so a
   * world always has cliffs of every height and the tall ones are always its
   * steepest places.
   *
   * AND THE CRAGS ARE OUT OF THE RECKONING. The percentile ran over every face
   * tile in the world and the small-formation pass then flattened 421 of the
   * 915 back to one -- so the tall end of the distribution was being spent on
   * lumps that get drawn as boulders, and the walls that remained were 76
   * visible north faces on a 128x96 island, nine of them three tiles tall.
   * Reported from play in four words: "what happened to all the tall cliffs".
   *
   * Cut over the walls only, and cut deeper: the steepest tenth are four tiles,
   * the next fifth three, the next three-tenths two. */
  {
    const vals = [...climbOf.values()].sort((a, b) => a - b);
    if (vals.length) {
      const cut2 = vals[Math.floor(vals.length * 0.40)];
      const cut3 = vals[Math.floor(vals.length * 0.70)];
      const cut4 = vals[Math.floor(vals.length * 0.90)];
      for (const [j, c] of climbOf) {
        // a crag is drawn as a boulder and its height is not paint but rock
        // claimed below it, so it stays at one tile however steep it stands
        face[j] = crag[j] ? 1
          : c >= cut4 ? 4 : c >= cut3 ? 3 : c >= cut2 ? 2 : 1;
      }
      /* AND A CLIFF HOLDS ITS HEIGHT ALONG ITS LENGTH.
       *
       * Cut per tile, the height wanders — this tile three, its neighbour two,
       * the next three again — and every change is a corner, so a wall came
       * out as a row of detached grey slabs rather than one face. Real rock
       * runs at a height for a while and then steps.
       *
       * Two passes of a median along the run does it: a tile takes the middle
       * value of itself and the faces either side, so single-tile wobble is
       * flattened out and genuine changes of height survive as a step. */
      for (let pass = 0; pass < 2; pass++) {
        const src = Uint8Array.from(face);
        for (let y = 0; y < TH; y++) {
          for (let x = 0; x < TW; x++) {
            const j = at(x, y);
            if (!src[j]) continue;
            const l = x > 0 ? src[at(x - 1, y)] : 0;
            const r = x < TW - 1 ? src[at(x + 1, y)] : 0;
            if (!l || !r) continue;             // the end of a run keeps its own
            const three = [l, src[j], r].sort((a, b) => a - b);
            face[j] = three[1];
          }
        }
      }
    }
  }

  /* A WALL THREE TILES HIGH STANDS ON THREE TILES OF GROUND.
   *
   * The height was paint for a while, hung down over the ground in front of
   * the cliff — which looks right until the player walks through it. A face
   * you can stand inside is not a face; LttP's cliffs are solid for the whole
   * of their drawn height and the ground below starts underneath them.
   *
   * So the drawn height is claimed as rock and the plateaus are labelled ONE
   * more time from it. That is the only ordering that works: the heights need
   * the settled terrace, the regions need the heights, and the two cannot be
   * computed together. Everything downstream — the ramps, the tiers, the
   * routes — is derived from this final labelling and so agrees with the
   * picture by construction. */
  /* ...AND ONLY WHERE A FACE IS ACTUALLY DRAWN DOWNWARD.
   *
   * The claim ran on every face tile, and only a tile with HIGHER GROUND TO
   * THE NORTH hangs a face over what is below it. A west-facing wall four
   * tiles tall was therefore claiming three tiles of rock to its SOUTH, where
   * nothing is drawn and nothing is falling -- a column of blockers standing
   * in open ground, at right angles to the wall that caused them. On the
   * whole-island render those came out as long thin dark bars scattered
   * through the meadows, and from play: "a lot of this doesn't make physical
   * sense". It did not.
   *
   * A side or back edge is one tile of rock, which is all it ever draws. */
  /* ...AND A TALL WALL DOES NOT EAT A SCREEN.
   *
   * Height is paint, and the paint is charged in real ground: a wall drawn four
   * tiles tall claims three more tiles of rock beneath its foot so the picture
   * has something solid to stand in. Measured over three worlds, on the worst
   * screens 72-88% of ALL the rock is that scaffolding -- one screen came out
   * 2% walkable, 40 tiles of rock of which only five are the wall the terrain
   * actually has. That is a screen the player cannot use, produced by a
   * rendering trick rather than by the landscape.
   *
   * So the claim is bounded by how much open ground is left around it. Where a
   * tile still has room, a wall keeps its full height; where the ground is
   * already mostly rock, it is drawn shorter and claims less.
   *
   * THE FOOT ALWAYS SURVIVES, which is what makes this safe. Lifting rock
   * outright took the gating proof from 301/301 to 1,067 failures, because the
   * fragments ARE the walls between plateaus. Capping the claim only thins a
   * barrier; it never opens one, because the tile the terrain called a foot is
   * still rock and still one tile thick. */
  /* MEASURED AS IT GOES, NOT BEFORE IT STARTS.
   *
   * The first cut took a snapshot of the rock before any claiming and asked it
   * how much room was left. Before claiming, the only rock is the feet -- the
   * ground still looks wide open everywhere, including on the screens that end
   * up 2% walkable -- so the cap fired on three tiles in three worlds. The
   * scarcity is CREATED by the claiming, so the claiming has to watch itself
   * do it: `face` is read live, and each wall that takes its full height
   * shortens the next one near it. */
  const R = 5;
  const roomAt = (x, y) => {
    let open = 0, seen = 0;
    for (let dy = -R; dy <= R; dy++) {
      for (let dx = -R; dx <= R; dx++) {
        const nx = x + dx, ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= TW || ny >= TH) continue;
        if (!land(nx, ny)) continue;
        seen++;
        if (!face[at(nx, ny)]) open++;
      }
    }
    return seen ? open / seen : 1;
  };
  for (let y = 0; y < TH; y++) {
    for (let x = 0; x < TW; x++) {
      const j = at(x, y);
      let h = face[j];
      if (h < 2) continue;
      const above = y > 0 ? terrace[at(x, y - 1)] : terrace[j];
      if (above <= terrace[j]) { face[j] = 1; continue; }
      const room = roomAt(x, y);
      const cap = room < 0.42 ? 1 : room < 0.58 ? 2 : room < 0.72 ? 3 : 4;
      if (h > cap) { h = cap; face[j] = h; }
      if (h < 2) continue;
      /* ...AND IT LEAVES A LEDGE ON THE WALL BELOW IT.
       *
       * The claim used to run down until it MET the next wall, and stopping at
       * the foot of that wall means taking the row directly above it -- which
       * is the ledge you would have stood on between the two drops. Measured
       * over three worlds: 56 of 754 walls sit directly on top of another with
       * no standable ground between, and only THREE of those are a genuine
       * double step in the terrace field. The other 53 are this: paint reaching
       * down onto the brink below it. Reported from play as cliffs stacked
       * directly on top of each other, and that is exactly what they are.
       *
       * So the claim stops one row short of another wall's brink, and the drawn
       * height comes down to match what it actually took -- otherwise the face
       * would hang over ground the player can walk on, which is the fault this
       * whole claim exists to prevent. */
      let took = 0;
      for (let k = 1; k < h; k++) {
        const ny = y + k;
        if (ny >= TH || !land(x, ny)) break;
        if (face[at(x, ny)]) break;               // already rock: the wall met another
        if (ny + 1 < TH && face[at(x, ny + 1)]
          && terrace[at(x, ny)] > terrace[at(x, ny + 1)]) break;
        face[at(x, ny)] = 1;
        took++;
      }
      face[j] = took + 1;
    }
  }
  };
  carve();
  footOverride = face;
  out = label();
  /* ...AND THE LAST LABELLING CAN MINT SLIVERS OF ITS OWN.
   *
   * The merge loop above absorbs plateaus too small to stand on, but it runs
   * BEFORE the faces are given their height — and claiming a wall's height as
   * rock cuts ground away, which can leave a two-tile shelf behind a cliff
   * that nothing then tidies up. Those become regions, regions carry a tier,
   * and a tier with a one-tile plateau in it is a tier the player is expected
   * to reach: seed-92 failed the gating proof with tier 3 held by regions of
   * two tiles and one.
   *
   * A ledge that small is not a place, it is part of the wall. Turning it to
   * rock and labelling again settles in a couple of rounds. */
  for (let guard = 0; guard < 8; guard++) {
    const small = out.regions.filter((r: any) => r.n < MIN);
    if (!small.length) break;
    let raised = 0;
    for (const r of small) {
      const contact = new Map();
      for (const p of out.pairs.values()) {
        if (p.a === r.r) contact.set(p.b, (contact.get(p.b) || 0) + p.tiles.length);
        if (p.b === r.r) contact.set(p.a, (contact.get(p.a) || 0) + p.tiles.length);
      }
      let into = -1, best = -1;
      for (const [q, n] of contact) if (n > best && !small.some((z: any) => z.r === q)) { best = n; into = q; }
      if (into < 0) {
        for (let j = 0; j < out.id.length; j++) if (out.id[j] === r.r) face[j] = 1;   // nothing to join: rock
        continue;
      }
      const lvl = out.regions.find((z: any) => z.r === into).lvl;
      /* WHOLE BLOCKS, not the open tiles alone. A shelf is its open tiles plus
       * the rock rim at its own level around them, and raising only the open
       * ones left them outvoted in their blocks by that rim: `blockTerraces`
       * put every one back, and depthlook's nine small regions came round
       * eight times unchanged. The terraces are block-aligned, so a shelf is
       * whole blocks, and it is the blocks that move. */
      /* ...AND A BLOCK IS ONE OF THE TERRACES' OWN CELLS (2026-09-17), not a square of a uniform grid: raise what
       * the vote below will read, or the vote puts it straight back. */
      const mine = new Uint8Array(out.id.length);
      for (let j = 0; j < out.id.length; j++) if (out.id[j] === r.r) mine[j] = 1;
      for (const [x0, x1, y0, y1] of terraceCells(TW, TH, t.blockSeed || 0)) {
        let hit = false;
        for (let y = y0; y < y1 && !hit; y++) for (let x = x0; x < x1; x++) if (mine[at(x, y)]) { hit = true; break; }
        if (!hit) continue;
        for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) if (fill[at(x, y)] >= sea) terrace[at(x, y)] = lvl;
      }
      raised++;
    }
    if (raised) { blockTerraces(terrace, fill, sea, TW, TH, at, t.blockSeed || 0); footOverride = null; carve(); footOverride = face; }
    out = label();
  }
  /* ROCK THAT SHOWS NO WALL AT ALL IS A BOULDER.
   *
   * Three passes turn ground into rock for reasons that have nothing to do with
   * a cliff being visible there: a wall claims the tiles under its drawn height,
   * the merge absorbs plateaus too small to stand on, and the last labelling
   * turns two-tile slivers behind a face into more of the face. All of them are
   * right about the COLLISION and none of them leaves anything to look at.
   *
   * Counted over three worlds, of 3,142 rock tiles: 753 are a foot with higher
   * ground to the north and draw a face, 1,636 have higher ground east, west or
   * south and draw an edge and a shadow, 511 are covered by a face hanging down
   * from above -- and 241 draw NOTHING. Solid, impassable, and painted as
   * ordinary stone lying flat in the grass. Reported from play as the tiles
   * around a cliff not making sense, which is exactly what they are: you walk
   * into a wall that is not there.
   *
   * A lone lump of rock in a field is a boulder, and the draw already knows how
   * to make one -- `crag` is the flag for it, and a crag keeps its collision,
   * its face height of one, and its place in the region graph. So the last word
   * on the rock is: if nothing about it will ever be drawn as a wall, it is an
   * outcrop. */
  {
    const lvAt = (x, y) => terrace[at(Math.max(0, Math.min(TW - 1, x)),
      Math.max(0, Math.min(TH - 1, y)))];
    const faceAt = (x, y) => face[at(Math.max(0, Math.min(TW - 1, x)),
      Math.max(0, Math.min(TH - 1, y)))];
    const crag = t.crag;
    for (let y = 0; y < TH; y++) {
      for (let x = 0; x < TW; x++) {
        const j = at(x, y);
        if (!face[j] || (crag && crag[j])) continue;
        const here = lvAt(x, y);
        // a side or a front: the projection can show something of it
        if (lvAt(x, y - 1) > here || lvAt(x + 1, y) > here
          || lvAt(x - 1, y) > here || lvAt(x, y + 1) > here) continue;
        // ...or a face hanging down over it from a foot further up the column
        let hidden = false;
        for (let k = 1; k <= 4 && !hidden; k++) {
          if (y - k < 0) break;
          if (!faceAt(x, y - k)) break;
          if (lvAt(x, y - k - 1) > lvAt(x, y - k) && faceAt(x, y - k) > k) hidden = true;
        }
        if (hidden) continue;
        /* ...BUT A MASS IS NOT A FIELD OF BOULDERS.
         *
         * The first cut of this cragged every tile that showed no wall, and a
         * mesa -- a broad flat top of rock with nothing higher anywhere beside
         * it -- is entirely made of such tiles. Nineteen of them in one heath
         * screen each drew their own boulder, inset from their own tile edges,
         * and the result was a five-by-four grid of cobbles with grass showing
         * between: the "brick wall" that survived two rounds of retuning the
         * stone tones, because it was never a question of tone.
         *
         * An outcrop is a LUMP. A tile with four or more of its eight
         * neighbours in rock is inside something bigger, and what it wants is
         * to be part of one mass -- which is what the stone art draws when
         * nothing else claims it. */
        let around = 0;
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1],
          [1, 1], [1, -1], [-1, 1], [-1, -1]]) {
          if (faceAt(x + dx, y + dy)) around++;
        }
        if (around >= 4) continue;
        if (crag) crag[j] = 1;
        face[j] = 1;
      }
    }
  }

  return { id: out.id, regions: out.regions, pairs: [...out.pairs.values()], foot: out.foot, isSea, coast };
}
